import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Target, Play, Pause, CheckCircle2, XCircle, Clock, AlertCircle, Loader2, Trash2, Eye, Brain, Rocket, Calendar } from "lucide-react";
import type { AgentMission, MissionUpdate, AiAgent } from "@shared/schema";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-gray-500", icon: Clock },
  active: { label: "Active", color: "bg-blue-500", icon: Rocket },
  paused: { label: "Paused", color: "bg-yellow-500", icon: Pause },
  completed: { label: "Completed", color: "bg-green-500", icon: CheckCircle2 },
  failed: { label: "Failed", color: "bg-red-500", icon: XCircle },
};

const PRIORITY_CONFIG = {
  low: { label: "Low", color: "secondary" as const },
  medium: { label: "Medium", color: "default" as const },
  high: { label: "High", color: "destructive" as const },
  urgent: { label: "Urgent", color: "destructive" as const },
};

export default function AgentMissions() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedMission, setSelectedMission] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("active");

  const [newMission, setNewMission] = useState({
    agentId: "",
    title: "",
    description: "",
    objective: "",
    priority: "medium",
    requiresApproval: false,
    deadline: "",
  });

  const { data: missions = [], isLoading } = useQuery<(AgentMission & { ai_agents?: { name: string; icon: string } })[]>({
    queryKey: ["/api/missions"],
  });

  const { data: agents = [] } = useQuery<AiAgent[]>({
    queryKey: ["/api/ai-agents"],
  });

  const { data: selectedMissionData } = useQuery<AgentMission & { updates: MissionUpdate[]; ai_agents?: { name: string; icon: string; persona: string } }>({
    queryKey: ["/api/missions", selectedMission],
    enabled: !!selectedMission,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newMission) => {
      return apiRequest("POST", "/api/missions", {
        ...data,
        requiresApproval: data.requiresApproval ? "true" : "false",
        deadline: data.deadline || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/missions"] });
      setIsCreateOpen(false);
      setNewMission({
        agentId: "",
        title: "",
        description: "",
        objective: "",
        priority: "medium",
        requiresApproval: false,
        deadline: "",
      });
      toast({ title: "Mission created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create mission", description: error.message, variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/missions/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/missions"] });
      if (selectedMission) {
        queryClient.invalidateQueries({ queryKey: ["/api/missions", selectedMission] });
      }
      toast({ title: "Mission updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/missions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/missions"] });
      if (selectedMission) setSelectedMission(null);
      toast({ title: "Mission deleted" });
    },
  });

  const activeMissions = missions.filter(m => m.status === "active" || m.status === "pending");
  const completedMissions = missions.filter(m => m.status === "completed");
  const failedMissions = missions.filter(m => m.status === "failed" || m.status === "paused");

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
  };

  const getPriorityConfig = (priority: string) => {
    return PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG] || PRIORITY_CONFIG.medium;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Agent Missions</h1>
          <p className="text-muted-foreground mt-1">Monitor and manage AI agent tasks</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-mission">
              <Plus className="h-4 w-4 mr-2" />
              New Mission
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Mission</DialogTitle>
              <DialogDescription>Assign a task to an AI agent</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Agent</Label>
                <Select
                  value={newMission.agentId}
                  onValueChange={(value) => setNewMission(prev => ({ ...prev, agentId: value }))}
                >
                  <SelectTrigger data-testid="select-agent">
                    <SelectValue placeholder="Choose an AI agent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2">
                          <span>{agent.icon}</span>
                          <span>{agent.name}</span>
                          <Badge variant="secondary" className="ml-2">{agent.specialty}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mission Title</Label>
                <Input
                  placeholder="e.g., Research market trends"
                  value={newMission.title}
                  onChange={(e) => setNewMission(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="input-mission-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of the mission..."
                  value={newMission.description}
                  onChange={(e) => setNewMission(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="input-mission-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Objective</Label>
                <Textarea
                  placeholder="What should the agent accomplish?"
                  value={newMission.objective}
                  onChange={(e) => setNewMission(prev => ({ ...prev, objective: e.target.value }))}
                  data-testid="input-mission-objective"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={newMission.priority}
                    onValueChange={(value) => setNewMission(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger data-testid="select-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Deadline (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={newMission.deadline}
                    onChange={(e) => setNewMission(prev => ({ ...prev, deadline: e.target.value }))}
                    data-testid="input-mission-deadline"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Requires Approval</Label>
                  <p className="text-xs text-muted-foreground">Agent will pause for approval at key points</p>
                </div>
                <Switch
                  checked={newMission.requiresApproval}
                  onCheckedChange={(checked) => setNewMission(prev => ({ ...prev, requiresApproval: checked }))}
                  data-testid="switch-mission-approval"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => createMutation.mutate(newMission)}
                disabled={!newMission.agentId || !newMission.title || !newMission.objective || createMutation.isPending}
                data-testid="button-save-mission"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Mission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Rocket className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeMissions.length}</p>
                <p className="text-sm text-muted-foreground">Active Missions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedMissions.length}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {missions.filter(m => m.requiresApproval === "true" && m.approvalStatus === "pending").length}
                </p>
                <p className="text-sm text-muted-foreground">Awaiting Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Brain className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agents.length}</p>
                <p className="text-sm text-muted-foreground">Available Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="active" data-testid="tab-active">
                Active ({activeMissions.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">
                Completed ({completedMissions.length})
              </TabsTrigger>
              <TabsTrigger value="failed" data-testid="tab-failed">
                Failed/Paused ({failedMissions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {activeMissions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <Target className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="font-medium mb-2">No Active Missions</h3>
                    <p className="text-sm text-muted-foreground mb-4">Create a mission to get your AI agents working</p>
                    <Button onClick={() => setIsCreateOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Mission
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {activeMissions.map(mission => {
                    const statusConfig = getStatusConfig(mission.status);
                    const priorityConfig = getPriorityConfig(mission.priority || "medium");
                    const StatusIcon = statusConfig.icon;
                    return (
                      <Card 
                        key={mission.id} 
                        className={`cursor-pointer transition-all hover-elevate ${selectedMission === mission.id ? "ring-2 ring-primary" : ""}`}
                        onClick={() => setSelectedMission(mission.id)}
                        data-testid={`card-mission-${mission.id}`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${statusConfig.color}/10`}>
                                <StatusIcon className={`h-5 w-5 text-${statusConfig.color.replace("bg-", "")}`} />
                              </div>
                              <div>
                                <CardTitle className="text-base flex items-center gap-2">
                                  {mission.title}
                                  <Badge variant={priorityConfig.color}>{priorityConfig.label}</Badge>
                                </CardTitle>
                                <CardDescription className="flex items-center gap-2 mt-1">
                                  {mission.ai_agents && (
                                    <>
                                      <span>{mission.ai_agents.icon}</span>
                                      <span>{mission.ai_agents.name}</span>
                                    </>
                                  )}
                                </CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {mission.status === "pending" && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateStatusMutation.mutate({ id: mission.id, status: "active" });
                                  }}
                                  data-testid={`button-start-mission-${mission.id}`}
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  Start
                                </Button>
                              )}
                              {mission.status === "active" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateStatusMutation.mutate({ id: mission.id, status: "paused" });
                                  }}
                                >
                                  <Pause className="h-4 w-4 mr-1" />
                                  Pause
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{mission.objective}</p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span>Progress</span>
                              <span>{mission.progress || 0}%</span>
                            </div>
                            <Progress value={parseInt(mission.progress || "0")} className="h-2" />
                          </div>
                          {mission.deadline && (
                            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>Due: {new Date(mission.deadline).toLocaleDateString()}</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed">
              {completedMissions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="font-medium">No Completed Missions</h3>
                    <p className="text-sm text-muted-foreground">Completed missions will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {completedMissions.map(mission => (
                    <Card 
                      key={mission.id}
                      className={`cursor-pointer transition-all hover-elevate ${selectedMission === mission.id ? "ring-2 ring-primary" : ""}`}
                      onClick={() => setSelectedMission(mission.id)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <div>
                              <CardTitle className="text-base">{mission.title}</CardTitle>
                              <CardDescription>
                                Completed {mission.completedAt ? new Date(mission.completedAt).toLocaleDateString() : ""}
                              </CardDescription>
                            </div>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMutation.mutate(mission.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="failed">
              {failedMissions.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <XCircle className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="font-medium">No Failed Missions</h3>
                    <p className="text-sm text-muted-foreground">Failed or paused missions will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {failedMissions.map(mission => {
                    const statusConfig = getStatusConfig(mission.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <Card 
                        key={mission.id}
                        className={`cursor-pointer transition-all hover-elevate ${selectedMission === mission.id ? "ring-2 ring-primary" : ""}`}
                        onClick={() => setSelectedMission(mission.id)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <StatusIcon className={`h-5 w-5 ${mission.status === "failed" ? "text-red-500" : "text-yellow-500"}`} />
                              <div>
                                <CardTitle className="text-base">{mission.title}</CardTitle>
                                <Badge variant={mission.status === "failed" ? "destructive" : "secondary"}>
                                  {statusConfig.label}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {mission.status === "paused" && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateStatusMutation.mutate({ id: mission.id, status: "active" });
                                  }}
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  Resume
                                </Button>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMutation.mutate(mission.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <div>
          {selectedMission && selectedMissionData ? (
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">Mission Details</CardTitle>
                <CardDescription>{selectedMissionData.title}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedMissionData.ai_agents && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <span className="text-2xl">{selectedMissionData.ai_agents.icon}</span>
                    <div>
                      <p className="font-medium">{selectedMissionData.ai_agents.name}</p>
                      <p className="text-xs text-muted-foreground">Assigned Agent</p>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-xs text-muted-foreground">Objective</Label>
                  <p className="text-sm mt-1">{selectedMissionData.objective}</p>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Progress</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Progress value={parseInt(selectedMissionData.progress || "0")} className="flex-1" />
                    <span className="text-sm font-medium">{selectedMissionData.progress || 0}%</span>
                  </div>
                </div>

                {selectedMissionData.updates && selectedMissionData.updates.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Recent Updates</Label>
                    <ScrollArea className="h-[200px] mt-2">
                      <div className="space-y-3">
                        {selectedMissionData.updates.slice(0, 5).map(update => (
                          <div key={update.id} className="p-2 bg-muted/50 rounded text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{update.updateType}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(update.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm">{update.message}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  {selectedMissionData.status === "active" && (
                    <Button
                      className="flex-1"
                      variant="outline"
                      onClick={() => updateStatusMutation.mutate({ id: selectedMission, status: "completed" })}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => deleteMutation.mutate(selectedMission)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Eye className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium">Select a Mission</h3>
                <p className="text-sm text-muted-foreground">Click on a mission to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
